import {
  isV2Duplicate,
  isV2Ok,
  v2Fetch,
  type V2Body,
} from '../net/v2Client.ts';

export type ApiResponse = V2Body<Record<string, unknown>> & {
  id?: string;
};

export type Deck = {
  id: string;
  name: string;
  numOfCards?: number;
};

export type Card = {
  id: string;
  deck: string;
  deckName?: string;
  front?: string;
  frontTrans?: string;
  refSent?: string;
  refTrans?: string;
  frontDesc?: string;
  backDesc?: string;
  nbsCache?: Record<string, string>;
  [key: string]: unknown;
};

export class CardsApiError extends Error {
  body: V2Body<unknown>;
  constructor(body: V2Body<unknown>) {
    const msg =
      body && body.ok === false
        ? body.error?.message || body.error?.code || 'cards api error'
        : 'cards api error';
    super(msg);
    this.name = 'CardsApiError';
    this.body = body;
  }
}

function withId(body: V2Body<{ id?: string }> | undefined): ApiResponse | undefined {
  if (!body) return undefined;
  if (isV2Ok(body) && body.data?.id) {
    return { ...body, id: body.data.id };
  }
  return body as ApiResponse;
}

export async function getDecks(
  userId: string,
  userToken: string,
  fetchImpl?: typeof fetch,
): Promise<Deck[]> {
  const json = await v2Fetch<{ decks: Deck[] }>('GET', '/v2/cards/decks', {
    query: { uid: userId },
    token: userToken,
    fetchImpl,
  });
  if (isV2Ok(json) && Array.isArray(json.data.decks)) {
    return json.data.decks;
  }
  throw new CardsApiError(json);
}

export async function getCards(
  userId: string,
  userToken: string,
  deckId: string,
  cursor: string | undefined,
  sort: string,
  fetchImpl?: typeof fetch,
): Promise<Card[]> {
  const json = await v2Fetch<{ cards: Card[] }>('GET', '/v2/cards/cards', {
    query: {
      uid: userId,
      deck: deckId,
      cursor,
      sort,
    },
    token: userToken,
    fetchImpl,
  });
  if (isV2Ok(json) && Array.isArray(json.data.cards)) {
    return json.data.cards;
  }
  throw new CardsApiError(json);
}

export async function removeCardById(
  userId: string,
  userToken: string,
  id: string,
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return v2Fetch('DELETE', `/v2/cards/cards/${encodeURIComponent(id)}`, {
    query: { uid: userId },
    token: userToken,
    fetchImpl,
  });
}

export async function createDeck(
  userId: string,
  userToken: string,
  name: string,
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return withId(
    await v2Fetch<{ id: string }>('POST', '/v2/cards/decks', {
      body: { uid: userId, token: userToken, name },
      token: userToken,
      fetchImpl,
    }),
  );
}

export async function removeDeck(
  userId: string,
  userToken: string,
  id: string,
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return v2Fetch('DELETE', `/v2/cards/decks/${encodeURIComponent(id)}`, {
    query: { uid: userId },
    token: userToken,
    fetchImpl,
  });
}

export async function findCardsByText(
  userId: string,
  userToken: string,
  text: string,
  deckId: string,
  fetchImpl?: typeof fetch,
): Promise<Card[]> {
  const json = await v2Fetch<{ cards: Card[] }>('GET', '/v2/cards/cards', {
    query: {
      uid: userId,
      token: userToken,
      text,
      deck: deckId,
    },
    token: userToken,
    fetchImpl,
  });
  if (isV2Ok(json) && Array.isArray(json.data.cards)) {
    return json.data.cards;
  }
  return [];
}

export async function patchCardCache(
  userId: string,
  userToken: string,
  cardId: string,
  key: string,
  value: string,
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return v2Fetch('PATCH', `/v2/cards/cards/${encodeURIComponent(cardId)}/cache`, {
    body: {
      uid: userId,
      token: userToken,
      key,
      value,
    },
    token: userToken,
    fetchImpl,
  });
}

export async function createCard(
  userId: string,
  userToken: string,
  payload: {
    deck: string;
    deckName: string;
    front: string;
    frontLang: string;
    frontTrans: string;
    transLang: string;
    refSent: string;
    refTrans: string;
    nbsCache?: Record<string, string>;
  },
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return withId(
    await v2Fetch<{ id: string }>('POST', '/v2/cards/cards', {
      body: {
        v2: true,
        skipDedupe: true,
        uid: userId,
        token: userToken,
        ...payload,
        frontDesc: '',
        backDesc: '',
      },
      token: userToken,
      fetchImpl,
    }),
  );
}

export async function updateCard(
  userId: string,
  userToken: string,
  card: Card,
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return v2Fetch('PUT', `/v2/cards/cards/${encodeURIComponent(card.id)}`, {
    body: {
      uid: userId,
      token: userToken,
      deck: card.deck,
      deckName: card.deckName,
      front: card.front,
      frontTrans: card.frontTrans,
      refSent: card.refSent,
      refTrans: card.refTrans,
      frontDesc: card.frontDesc,
      backDesc: card.backDesc,
    },
    token: userToken,
    fetchImpl,
  });
}

export async function downloadCSV(
  userId: string,
  userToken: string,
  deck: Deck,
  fetchImpl?: typeof fetch,
): Promise<ApiResponse | undefined> {
  return v2Fetch<{ url: string }>('POST', '/v2/cards/export.csv', {
    body: { uid: userId, token: userToken, deck: deck.id },
    token: userToken,
    fetchImpl,
  });
}

export { isV2Duplicate, isV2Ok };
